import type { EngineAudio } from '../audio/engine';
import type { InputManager } from '../input/input';
import { loadProfile } from '../net/api';
import { SLOT_COLORS, buildMatch, buildRace, lineup } from '../net/lineup';
import { NetSim, RollbackSession } from '../net/netcode';
import { OnlineClient, StartInfo } from '../net/online';
import {
  HASH_EVERY, INPUT_DELAY, LAP_CHOICES, LIGHT_CHOICES, MAX_PLAYERS, MAX_PREDICT, OnlineSettings, RoomInfo, cleanSettings,
} from '../net/protocol';
import { Renderer, lerpCar } from '../render/renderer';
import { trackMapSvg } from '../render/trackArt';
import { DRIVERS, DriverSpec } from '../sim/drivers';
import { aiInput } from '../sim/ai';
import { CarState, quantise, speedOf } from '../sim/car';
import { COUCH_VIEW_H, COUCH_VIEW_W, CouchEvent, CouchMatch } from '../sim/couch';
import { trackById } from '../sim/daily';
import { RaceEvent, RaceWorld } from '../sim/race';
import { loadCouch, loadRace, saveCouch, saveRace } from '../sim/snapshot';
import { teamById } from '../sim/teams';
import { CALENDAR } from '../sim/tracks/index';
import { TICK_HZ, mphFromPxs } from '../sim/tuning';
import { fmtTime } from './format';

/**
 * Online play front end (design.md B8.2, M7): invite links (/race/CODE),
 * the room lobby, and races between devices. The netcode is
 * src/net/netcode.ts; the server just relays inputs (server/src/rooms.ts).
 */

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const hex = (n: number) => `#${n.toString(16).padStart(6, '0')}`;

export interface OnlineHost {
  renderer: Renderer;
  audio: EngineAudio;
  input: InputManager;
  sheet(kicker: string, title: string, sub: string, body: HTMLElement | string, actions: { label: string; primary?: boolean; onClick: () => void }[]): void;
  banner(text: string, sub: string, cls: 'good' | 'bad' | '', secs: number): void;
  hideAll(): void;
  /** Back to the title screen (the online session is already closed). */
  home(): void;
  /** Show a path in the address bar without navigating. */
  setUrl(path: string): void;
  /** Hand the screen to an online race (HUD, touch controls). */
  enterRace(touch: boolean): void;
  /** Track picked on the title screen (default for new rooms). */
  defaultTrack(): string;
  /** Draw this circuit (the room's track, which may not be this device's title-screen pick). */
  showTrack(trackId: string): void;
}

type Sim = RaceWorld | CouchMatch;

interface NetRace {
  start: StartInfo;
  kind: 'race' | 'h2h';
  sim: Sim;
  session: RollbackSession<unknown>;
  /** My racer index (-1 = watching). */
  me: number;
  prevCars: CarState[];
  names: string[];
  colors: number[];
  finished: boolean;
  /** Tick at which the (possibly predicted) race first looked over; -1 if not. */
  overAt: number;
  lightsShown: number;
  go: boolean;
  flashes: { x: number; y: number; f: number }[];
  hudKey: string;
}

const NAME_KEY = 'sms.netname';
const AUTO: DriverSpec = { ...DRIVERS[0], id: 'auto', shortcut: 0, mistakeRate: 0, consistency: 1 };

export class OnlineMode {
  private host: OnlineHost;
  private client: OnlineClient | null = null;
  private room: RoomInfo | null = null;
  private race: NetRace | null = null;
  private view: 'none' | 'lobby' | 'race' | 'result' | 'waiting' = 'none';
  private pendingCode: string | null = null;
  autoDrive = false;

  constructor(host: OnlineHost) {
    this.host = host;
  }

  /** In a room (lobby, race or results). */
  get active(): boolean {
    return this.client !== null;
  }

  /** A race is running (the Game hands its frame loop over). */
  get racing(): boolean {
    return this.view === 'race' && this.race !== null;
  }

  get debugRace(): NetRace | null {
    return this.race;
  }

  // ================================================================ entry

  /** New room: you're the host. */
  create(): void {
    this.withName((name, color) => {
      this.connect();
      this.client!.create(name, color);
      this.showConnecting('CREATING YOUR ROOM…');
    });
  }

  /** Invite link: /race/CODE. */
  join(code: string): void {
    this.pendingCode = code;
    this.withName((name, color) => {
      this.connect();
      this.client!.join(code, name, color);
      this.showConnecting(`JOINING ${code}…`);
    });
  }

  /** Leave the room and go home. */
  leave(): void {
    this.disconnect();
    this.host.setUrl('/');
    this.host.home();
  }

  /** Close the connection without navigating (the Game is going elsewhere). */
  disconnect(): void {
    this.client?.close();
    this.client = null;
    this.room = null;
    this.race = null;
    this.view = 'none';
    $('lights').hidden = true;
  }

  private connect(): void {
    this.client?.close();
    this.client = new OnlineClient({
      room: (r) => this.onRoom(r),
      start: (s) => this.onStart(s),
      input: (slot, f, list) => this.race?.session.addRemote(slot, f, list),
      gone: (slot, f) => {
        if (!this.race) return;
        this.race.session.markGone(slot, f);
        if (slot !== this.race.me) this.host.banner(`${this.race.names[slot]} LEFT`, 'The AI takes their car', 'bad', 2);
        else this.host.banner('CONNECTION TOO SLOW', 'The AI has your car: you can keep watching', 'bad', 3);
      },
      error: (msg, code) => this.onError(msg, code),
      desync: (f) => {
        console.warn(`online race out of sync at tick ${f}`);
        this.host.banner('OUT OF SYNC', 'Positions may differ between screens', 'bad', 3);
      },
      closed: () => {
        if (!this.client) return;
        this.client = null;
        this.race = null;
        this.view = 'none';
        this.host.sheet('ONLINE', 'CONNECTION LOST', 'The link to the server dropped.', '', [
          { label: 'REJOIN', primary: true, onClick: () => (this.room ? this.join(this.room.code) : this.host.home()) },
          { label: 'HOME', onClick: () => this.leave() },
        ]);
      },
    });
  }

  private withName(go: (name: string, color: number) => void): void {
    const p = loadProfile();
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(NAME_KEY);
    } catch {
      /* private mode */
    }
    const name = p?.initials ?? saved;
    if (name) return go(name, p?.color ?? 0);
    const body = document.createElement('div');
    body.innerHTML = `<p class="tag">Three letters, shown above your car.</p>
      <input id="net-ini" class="ini-input" maxlength="3" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="AAA" />
      <p class="muted center" id="net-msg"></p>`;
    const ok = () => {
      const v = ($('net-ini') as HTMLInputElement).value;
      if (!/^[A-Z]{3}$/.test(v)) {
        $('net-msg').textContent = 'Three letters, please.';
        return;
      }
      try {
        localStorage.setItem(NAME_KEY, v);
      } catch {
        /* fine */
      }
      go(v, 0);
    };
    this.host.sheet('ONLINE RACE', 'YOUR INITIALS', '', body, [
      { label: 'OK', primary: true, onClick: ok },
      { label: 'CANCEL', onClick: () => this.leave() },
    ]);
    const input = $('net-ini') as HTMLInputElement;
    input.oninput = () => (input.value = input.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3));
    input.onkeydown = (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') ok();
    };
    setTimeout(() => input.focus(), 50);
  }

  private showConnecting(title: string): void {
    this.view = 'none';
    this.host.sheet('ONLINE', title, 'Connecting to the race server…', '', [{ label: 'CANCEL', onClick: () => this.leave() }]);
  }

  private onError(msg: string, code?: string): void {
    if (code === 'noroom' || code === 'full' || code === 'version' || !this.room) {
      this.disconnect();
      this.host.setUrl('/');
      this.host.sheet('ONLINE', code === 'full' ? 'RACE FULL' : code === 'noroom' ? 'LINK EXPIRED' : 'CAN’T JOIN', msg, '', [
        { label: 'START A NEW ROOM', primary: true, onClick: () => this.create() },
        { label: 'HOME', onClick: () => this.leave() },
      ]);
      return;
    }
    this.host.banner(msg, '', 'bad', 2.5);
  }

  // ================================================================ lobby

  private onRoom(r: RoomInfo): void {
    const first = !this.room;
    this.room = r;
    this.host.setUrl(`/race/${r.code}`);
    if (first && r.you === r.host && this.pendingCode === null) {
      // A brand-new room: start from the track picked on the title screen.
      this.client?.send({ t: 'set', settings: { track: this.host.defaultTrack() } });
    }
    this.pendingCode = null;
    if (this.view !== 'race') this.host.showTrack(r.settings.track);
    const me = r.players.find((p) => p.id === r.you);
    if (r.state === 'racing' && me && !me.racing && this.view !== 'race') {
      this.view = 'waiting';
      this.renderWaiting();
      return;
    }
    if (this.view === 'none' || this.view === 'lobby' || this.view === 'waiting') {
      if (r.state === 'lobby') this.renderLobby();
    }
  }

  private renderWaiting(): void {
    this.host.sheet(`ONLINE · ROOM ${this.room!.code}`, 'RACE IN PROGRESS', "You're in the next one. Hang on!", this.playersHtml(), [
      { label: 'LEAVE', onClick: () => this.leave() },
    ]);
  }

  private playersHtml(): HTMLElement {
    const r = this.room!;
    const ul = document.createElement('div');
    ul.className = 'slots';
    for (let i = 0; i < MAX_PLAYERS; i++) {
      const p = r.players[i];
      const card = document.createElement('div');
      card.className = 'slot' + (p ? '' : ' empty');
      if (p) {
        const col = hex(SLOT_COLORS[i]);
        card.innerHTML = `<div class="slot-tag" style="color:${col}">${esc(p.name)}</div><div class="slot-car" style="background:${col}"></div>
          <div class="slot-who">${p.id === r.host ? 'HOST' : 'PLAYER'}${p.id === r.you ? ' &middot; YOU' : ''}${p.connected ? '' : ' &middot; GONE'}</div>`;
      } else {
        card.innerHTML = `<div class="slot-tag">?</div><div class="slot-who">SEND THE<br>LINK</div>`;
      }
      ul.appendChild(card);
    }
    return ul;
  }

  private renderLobby(): void {
    this.view = 'lobby';
    const r = this.room!;
    const s = r.settings;
    const isHost = r.you === r.host;
    const link = `${location.origin}/race/${r.code}`;
    const wrap = document.createElement('div');
    wrap.className = 'couch-lobby';
    const invite = document.createElement('div');
    invite.className = 'invite';
    invite.innerHTML = `<div class="muted">SEND THIS LINK: friends join on their own phone or computer</div>
      <div class="big-code">${r.code}</div><div class="invite-link">${esc(link)}</div>
      <div class="row-buttons"><button class="secondary" data-a="copy">COPY LINK</button>${typeof navigator.share === 'function' ? '<button class="secondary" data-a="share">SHARE</button>' : ''}</div>`;
    wrap.appendChild(invite);
    wrap.appendChild(this.playersHtml());

    const opts = document.createElement('div');
    opts.className = 'couch-opts';
    const set = (patch: Partial<OnlineSettings>) => this.client?.send({ t: 'set', settings: { ...s, ...patch } });
    const btn = (label: string, fn: () => void) => {
      const b = document.createElement('button');
      b.className = 'secondary';
      b.innerHTML = label;
      b.disabled = !isHost;
      b.onclick = fn;
      opts.appendChild(b);
    };
    btn(`MODE: ${s.mode === 'race' ? 'RACE' : 'HEAD TO HEAD'}`, () => set({ mode: s.mode === 'race' ? 'h2h' : 'race', cpus: 0 }));
    btn(`CAR: ${teamById(s.car).name}`, () => {
      const cars = ['marlin', 'bramley', 'lotos', 'ferro'];
      set({ car: cars[(cars.indexOf(s.car) + 1) % cars.length] });
    });
    if (s.mode === 'race') btn(`LAPS: ${s.laps}`, () => set({ laps: LAP_CHOICES[(LAP_CHOICES.indexOf(s.laps) + 1) % LAP_CHOICES.length] }));
    else btn(`LIGHTS: ${s.lights}`, () => set({ lights: LIGHT_CHOICES[(LIGHT_CHOICES.indexOf(s.lights) + 1) % LIGHT_CHOICES.length] }));
    const maxCpu = cleanSettings({ ...s, cpus: 99 }, s).cpus;
    btn(`CPUS: ${s.cpus}`, () => set({ cpus: s.cpus >= maxCpu ? 0 : s.cpus + 1 }));
    wrap.appendChild(opts);

    const track = trackById(s.track);
    const trk = document.createElement('div');
    trk.className = 'couch-track';
    trk.innerHTML = `<button class="arrow" data-d="-1" ${isHost ? '' : 'disabled'} aria-label="Previous track">&#9664;</button>
      <div class="t-map">${trackMapSvg(track, 40)}</div>
      <div><div class="track-name">${esc(track.def.name)}</div><div class="muted">${esc(track.def.country)} &middot; ${s.mode === 'race' ? `${s.laps} laps` : `${s.lights} lights each`}</div></div>
      <button class="arrow" data-d="1" ${isHost ? '' : 'disabled'} aria-label="Next track">&#9654;</button>`;
    trk.querySelectorAll<HTMLButtonElement>('button').forEach((b) => {
      b.onclick = () => {
        const i = CALENDAR.findIndex((d) => d.id === s.track);
        set({ track: CALENDAR[(i + Number(b.dataset.d) + CALENDAR.length) % CALENDAR.length].id });
      };
    });
    wrap.appendChild(trk);

    const humans = r.players.filter((p) => p.connected).length;
    const canStart = humans >= 2 || s.cpus >= 1;
    const sub = isHost
      ? canStart
        ? 'Ready when you are.'
        : 'Waiting for a friend to open the link (or add a CPU).'
      : `Waiting for ${esc(r.players.find((p) => p.id === r.host)?.name ?? 'the host')} to start.`;
    this.host.sheet(`ONLINE · ${s.mode === 'race' ? 'RACE' : 'HEAD TO HEAD'}`, `ROOM ${r.code}`, sub, wrap, [
      ...(isHost ? [{ label: 'START', primary: true, onClick: () => canStart && this.client?.send({ t: 'start' }) }] : []),
      { label: 'LEAVE', onClick: () => this.leave() },
    ]);
    const start = $('sheet-actions').querySelector('button.primary') as HTMLButtonElement | null;
    if (start) start.disabled = !canStart;
    wrap.querySelector<HTMLButtonElement>('[data-a="copy"]')!.onclick = (e) => {
      void navigator.clipboard?.writeText(link).then(() => ((e.target as HTMLButtonElement).textContent = 'COPIED!'));
    };
    const sh = wrap.querySelector<HTMLButtonElement>('[data-a="share"]');
    if (sh) sh.onclick = () => void navigator.share({ title: 'Steal My Seat', text: 'Race me!', url: link }).catch(() => undefined);
  }

  // ================================================================= race

  private onStart(start: StartInfo): void {
    const r = this.room;
    if (!r) return;
    const mine = start.roster.find((x) => x.player === r.you);
    if (!mine) {
      this.view = 'waiting';
      this.renderWaiting();
      return;
    }
    const kind = start.settings.mode;
    const l = lineup(start.settings, start.roster, start.seed);
    const sim: Sim = kind === 'race' ? buildRace(start.settings, l, start.seed) : buildMatch(start.settings, l, start.seed);
    const race = sim instanceof CouchMatch ? sim.race : sim;
    const nr: NetRace = {
      start,
      kind,
      sim,
      session: null as unknown as RollbackSession<unknown>,
      me: mine.slot,
      prevCars: race.racers.map((x) => ({ ...x.car })),
      names: l.specs.map((x) => x.tag),
      colors: l.specs.map((x, i) => (x.driver ? 0xffffff : SLOT_COLORS[i])),
      finished: false,
      overAt: -1,
      lightsShown: -1,
      go: false,
      flashes: [],
      hudKey: '',
    };
    const net: NetSim<unknown> = {
      step: (inputs) => {
        nr.prevCars = race.racers.map((x) => ({ ...x.car }));
        sim.step(inputs);
      },
      save: () => (sim instanceof CouchMatch ? saveCouch(sim) : saveRace(sim)),
      load: (s) => (sim instanceof CouchMatch ? loadCouch(sim, s as ReturnType<typeof saveCouch>) : loadRace(sim, s as ReturnType<typeof saveRace>)),
      hash: () => sim.hash(),
    };
    nr.session = new RollbackSession(net, { slots: l.humanSlots, local: mine.slot, inputDelay: INPUT_DELAY, maxPredict: MAX_PREDICT, checkEvery: HASH_EVERY });
    nr.session.onHash = (f, h) => this.client?.send({ t: 'hash', f, h });
    nr.session.onStep = (_f, resim) => {
      if (!resim) this.onEvents(nr);
    };
    this.race = nr;
    this.view = 'race';
    const r0 = race;
    // Every device must draw the room's circuit, not whatever its own title screen last showed.
    this.host.showTrack(start.settings.track);
    this.host.enterRace(document.body.classList.contains('touch'));
    if (kind === 'race') {
      this.host.renderer.setRivals(r0.racers.filter((x) => x.index !== nr.me).map((x) => ({ team: x.spec.team, tag: x.spec.tag, color: nr.colors[x.index] })));
      this.host.renderer.setTeam(r0.racers[nr.me].spec.team);
      $('hud').hidden = false;
      $('tower').hidden = false;
      $('hud-pos').hidden = false;
    } else {
      this.host.renderer.setRivals(r0.racers.map((x) => ({ team: x.spec.team, tag: x.spec.tag, color: nr.colors[x.index] })));
      $('couch-hud').hidden = false;
      this.buildCouchHud(nr);
    }
    this.host.renderer.setGhost(null);
    this.host.renderer.clearSkids();
    this.host.audio.unlock();
  }

  private onEvents(nr: NetRace): void {
    const sim = nr.sim;
    const race = sim instanceof CouchMatch ? sim.race : sim;
    const events: (RaceEvent | CouchEvent)[] = sim instanceof CouchMatch ? sim.events : sim.events;
    for (const e of events) {
      switch (e.type) {
        case 'crash': {
          const c = race.racers[e.racer].car;
          nr.flashes.push({ x: c.x, y: c.y, f: 1 });
          if (e.racer === nr.me) {
            this.host.audio.crash();
            if (navigator.vibrate) navigator.vibrate(120);
          }
          break;
        }
        case 'lap':
          if (e.racer === nr.me && nr.kind === 'race') {
            const r = race.racers[nr.me];
            if (r.lap.lapsDone < race.opts.laps) this.host.banner(r.lap.lapsDone + 1 === race.opts.laps ? 'FINAL LAP' : `LAP ${r.lap.lapsDone + 1}/${race.opts.laps}`, fmtTime(e.ticks), e.best ? 'good' : '', 1.4);
          }
          break;
        case 'finish':
          if (e.racer === nr.me) this.host.banner(e.position === 1 ? 'YOU WIN!' : `FINISHED P${e.position}`, '', e.position <= 3 ? 'good' : '', 3);
          break;
        case 'dropped':
          this.host.banner(`${nr.names[e.racer]} DROPPED`, e.lightsLeft > 0 ? `${e.lightsLeft} light${e.lightsLeft === 1 ? '' : 's'} left` : 'OUT OF THE MATCH', e.racer === nr.me ? 'bad' : '', 1.4);
          this.host.audio.blip(196, 0.2);
          break;
        case 'roundOver':
          this.host.banner(`${nr.names[e.winner]} WINS THE ROUND`, e.gained ? '+1 light' : '', e.winner === nr.me ? 'good' : '', 2);
          break;
        case 'roundStart':
          nr.prevCars = race.racers.map((x) => ({ ...x.car }));
          this.host.renderer.clearSkids();
          break;
        case 'go':
          this.host.banner('GO!', '', 'good', 0.6);
          this.host.audio.blip(1320, 0.15);
          break;
        default:
          break;
      }
    }
  }

  /** One animation frame: catch the sim up to the server clock, then draw. */
  frame(dtFrame: number): void {
    const nr = this.race;
    const client = this.client;
    if (!nr || !client) return;
    const sim = nr.sim;
    const race = sim instanceof CouchMatch ? sim.race : sim;
    const t = ((client.serverNow() - nr.start.startAt) * TICK_HZ) / 1000;

    // Starting lights before tick 0 (race mode; Head to Head counts itself down).
    if (nr.kind === 'race') {
      const lit = t >= 0 ? 6 : Math.max(0, Math.min(5, Math.floor((t + 2.6 * TICK_HZ) / (0.4 * TICK_HZ))));
      if (t < 0 && lit !== nr.lightsShown) {
        nr.lightsShown = lit;
        $('lights').hidden = false;
        document.querySelectorAll('.light').forEach((el, i) => el.classList.toggle('on', i < lit));
        if (lit > 0) this.host.audio.blip(440, 0.08);
      }
      if (t >= 0 && !nr.go) {
        nr.go = true;
        $('lights').hidden = true;
        this.host.banner('GO!', '', 'good', 0.6);
        this.host.audio.blip(1320, 0.15);
      }
    }

    const target = Math.max(0, Math.floor(t));
    nr.session.advanceTo(
      target,
      () => {
        if (this.autoDrive) {
          const r = race.racers[nr.me];
          return aiInput(r.ai, AUTO, {
            car: r.car, perf: r.perf, track: race.track, lapsDone: r.lap.lapsDone, basePace: 1.2,
            traffic: race.racers.map((o) => ({ car: o.car, solid: o.status === 'running' && !o.car.crashed })),
          });
        }
        const c = this.host.input.read();
        return quantise(c.steer, c.throttle, c.brake);
      },
      (f, inp) => client.queueInput(f, inp),
    );
    client.flush();

    // Draw.
    const alpha = Math.max(0, Math.min(1, t - nr.session.frame + 1));
    for (const f of nr.flashes) f.f -= dtFrame * 1.6;
    nr.flashes = nr.flashes.filter((f) => f.f > 0);
    if (sim instanceof CouchMatch) {
      const cars = race.racers.map((r, i) => ({ ...lerpCar(nr.prevCars[i] ?? r.car, r.car, alpha), alpha: sim.inRound[i] ? (r.ghostTicks > 0 ? 0.5 : 1) : 0.15 }));
      const danger = sim.offTicks.some((x) => x > 0) || sim.boxScale < 1;
      this.host.renderer.drawShared(sim.camX, sim.camY, COUCH_VIEW_W, COUCH_VIEW_H, sim.boxScale, cars, nr.flashes, danger);
      this.couchHud(nr, sim);
    } else {
      const me = race.racers[nr.me];
      const meView = lerpCar(nr.prevCars[nr.me] ?? me.car, me.car, alpha);
      const rivals = race.racers
        .filter((r) => r.index !== nr.me)
        .map((r) => ({ ...lerpCar(nr.prevCars[r.index] ?? r.car, r.car, alpha), alpha: r.status === 'out' ? 0 : r.ghostTicks > 0 || r.status === 'finished' ? 0.5 : 1 }));
      this.host.renderer.draw(meView, null, dtFrame, nr.flashes, rivals);
      this.raceHud(nr, race);
    }
    const mine = race.racers[nr.me];
    this.host.audio.update(Math.min(1, speedOf(mine.car) / mine.perf.vmax), 0.8, true);

    // Over, and confirmed (everyone's inputs up to that tick are in, so no
    // rollback can undo it)?
    const over = sim instanceof CouchMatch ? sim.phase === 'over' : sim.over;
    if (!over) nr.overAt = -1;
    else if (nr.overAt < 0) nr.overAt = nr.session.frame;
    if (over && !nr.finished && nr.session.confirmedFrame() >= nr.overAt) {
      nr.finished = true;
      client.send({ t: 'done' });
      setTimeout(() => this.showResult(nr), 1800);
    }
  }

  // ================================================================== HUD

  private raceHud(nr: NetRace, race: RaceWorld): void {
    const order = race.standings();
    const me = race.racers[nr.me];
    const pos = order.indexOf(nr.me) + 1;
    const laps = me.status === 'finished' ? 'FINISHED' : `LAP ${Math.min(me.lap.lapsDone + 1, race.opts.laps)}/${race.opts.laps}`;
    const key = `${pos}|${laps}|${order.join(',')}`;
    $('hud-speed').textContent = String(Math.round(mphFromPxs(speedOf(me.car))));
    $('hud-time').textContent = fmtTime(me.lap.startTick < 0 ? 0 : race.tick - me.lap.startTick);
    if (key === nr.hudKey) return;
    nr.hudKey = key;
    $('hud-pos').innerHTML = `P${pos}<small>/${order.length}</small>`;
    $('hud-laps').textContent = laps;
    $('tower').innerHTML = order
      .map((i, k) => {
        const r = race.racers[i];
        return `<li class="${i === nr.me ? 'me' : ''}${r.status === 'out' ? ' out' : ''}"><span>${k + 1}</span><span class="chip" style="background:${hex(r.spec.team.primary)}"></span><span style="color:${hex(nr.colors[i])}">${esc(r.spec.tag)}</span><span class="st">${r.status === 'finished' ? '✓' : ''}</span></li>`;
      })
      .join('');
  }

  private buildCouchHud(nr: NetRace): void {
    const ul = $('couch-hud');
    ul.innerHTML = nr.names.map((n, i) => `<li><span class="ch-tag" style="color:${hex(nr.colors[i])}">${esc(n)}</span><span class="ch-pips"></span></li>`).join('') + '<li class="ch-round"></li>';
  }

  private couchHud(nr: NetRace, m: CouchMatch): void {
    const lead = m.leader();
    const key = `${m.round}|${lead}|${m.lights.join(',')}|${m.inRound.join(',')}|${m.offTicks.map((t) => (t > 0 ? 1 : 0)).join('')}`;
    if (key === nr.hudKey) return;
    nr.hudKey = key;
    const items = $('couch-hud').children;
    m.racers.forEach((_, i) => {
      const li = items[i] as HTMLElement;
      li.className = [m.lights[i] <= 0 ? 'out' : '', !m.inRound[i] && m.lights[i] > 0 ? 'dropped' : '', i === lead && m.phase === 'racing' ? 'lead' : '', m.offTicks[i] > 0 ? 'off' : '']
        .filter(Boolean)
        .join(' ');
      (li.querySelector('.ch-pips') as HTMLElement).textContent = m.lights[i] > 0 ? '●'.repeat(Math.min(m.lights[i], 12)) : 'OUT';
    });
    (items[m.racers.length] as HTMLElement).textContent = `ROUND ${m.round}`;
  }

  // =============================================================== result

  private showResult(nr: NetRace): void {
    if (this.race !== nr) return;
    this.view = 'result';
    const sim = nr.sim;
    const isHost = this.room?.you === this.room?.host;
    const t = document.createElement('table');
    t.className = 'board';
    let title: string;
    if (sim instanceof CouchMatch) {
      const order = sim.racers.map((_, i) => i).sort((a, b) => (b === sim.winner ? 1 : 0) - (a === sim.winner ? 1 : 0) || sim.lights[b] - sim.lights[a] || sim.roundWins[b] - sim.roundWins[a]);
      t.innerHTML = '<tr><th>POS</th><th>DRIVER</th><th class="num">ROUNDS</th><th class="num">LIGHTS</th></tr>' +
        order.map((i, k) => `<tr class="${i === nr.me ? 'me' : ''}"><td>${k + 1}</td><td style="color:${hex(nr.colors[i])}">${esc(nr.names[i])}</td><td class="num">${sim.roundWins[i]}</td><td class="num">${sim.lights[i]}</td></tr>`).join('');
      title = sim.winner === nr.me ? 'YOU WIN!' : `${nr.names[sim.winner]} WINS`;
    } else {
      const res = sim.results();
      const win = res[0].timeTicks;
      t.innerHTML = '<tr><th>POS</th><th>DRIVER</th><th class="num">TIME</th><th class="num">BEST LAP</th></tr>' +
        res.map((r) => `<tr class="${r.racer === nr.me ? 'me' : ''}"><td>${r.position}</td><td style="color:${hex(nr.colors[r.racer])}">${esc(nr.names[r.racer])}</td><td class="num">${
          r.timeTicks === null ? 'DNF' : r.position === 1 ? fmtTime(r.timeTicks) : `+${((r.timeTicks - (win ?? 0)) / TICK_HZ).toFixed(2)}`
        }</td><td class="num">${fmtTime(r.bestLapTicks)}</td></tr>`).join('');
      const mine = res.find((r) => r.racer === nr.me)!;
      title = mine.position === 1 ? 'YOU WIN!' : `YOU FINISHED P${mine.position}`;
    }
    this.host.audio.update(0, 0, false);
    this.host.sheet(`ONLINE · ROOM ${this.room?.code ?? ''} · RESULT`, title, trackById(nr.start.settings.track).def.name, t, [
      ...(isHost
        ? [{ label: 'RACE AGAIN', primary: true, onClick: () => { this.client?.send({ t: 'lobby' }); this.client?.send({ t: 'start' }); } }]
        : []),
      { label: 'ROOM', primary: !isHost, onClick: () => this.backToRoom() },
      { label: 'LEAVE', onClick: () => this.leave() },
    ]);
  }

  private backToRoom(): void {
    this.race = null;
    if (this.room?.state === 'racing') {
      this.view = 'waiting';
      this.renderWaiting();
    } else if (this.room) this.renderLobby();
  }

  /** Pause menu → LEAVE RACE. */
  leaveRace(): void {
    this.leave();
  }
}
